import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select from "../common/components/Select";
import useClientConfig from "../common/hooks/clientConfigHook";
import { dynamicSort } from "../common/utils/tableUtilities";
import useCredentials from "../common/hooks/credentialsHook";
import { useTypesForSiteLevel } from "../reducers/typeSlice";
import PropTypes from 'prop-types';
import { distinct } from "../common/utils/arrayUtilities";
import Label from "../common/components/Label";
import { propagateFields } from "./customCtrlUtils";

export const customCtrlDef = {
    control: ({ def, onChange, type, site, level, context: { args } = {}, ...props }) =>
        <TypeDropdown perm={def.perm} type={type} level={level ?? args?.level ?? def.level} site={site ?? args?.site ?? def.site} wildcard={def.wildcard} filter={def.filter} strict={def.strict} mode={def.mode} credentialsRoot={def.credentialsRoot} onChange={value => propagateFields(value, def.field, onChange)} {...props}></TypeDropdown>
}

export const getDisplayTypes = (types, credentials, site, level, type, perm, mode, filter, strict) => {
    let displayTypes = types.filter(t => (!mode || (mode === 'create' && (t.permitCreate === true || t.permitCreate === undefined)) || (mode === 'transfer' && (t.permitTransfer === true || t.permitTransfer === undefined))) && (!filter || filter(t)) && ((t.type === type && (!level || !t.level || t.level === level) && (!site || !t.site || t.site === site)) || perm === 'any' || credentials[perm === 'write' ? 'hasFull' : 'has']({ site: t.site ?? site, level: t.level ?? level, type: t.type })));
    if (type && displayTypes.every(t => t.type !== type && (level && type.level && level !== type.level) && (site && type.site && site !== type.site))) {
        const toAdd = types.find(t => ((t.type === type && (!level || !t.level || t.level === level) && (!site || !t.site || t.site === site))));
        if (toAdd) {
            displayTypes.push(toAdd);
        }
    }
    displayTypes.sort(dynamicSort(['sequence','type','site','level']));
    if (!strict) {
        displayTypes = displayTypes.map(t => ({ type: t.type, internalid: t.internalid }));
        displayTypes = distinct(displayTypes, 'type');
    }
    return displayTypes;
}

const TypeDropdownImpl = ({ id, site, level, type, perm, filter, placeholder, wildcard, hideLabel = false, disabled = false, readonly = false, required = false, onChange, mode, credentialsRoot = 'access', strict = true, additionalTypes = [], autoHide=false }) => {
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const { t } = useTranslation();
    const types = useTypesForSiteLevel(clientConfig.enableSites ? site : null, clientConfig.enableLevels ? level : null);

    const userTypes = useMemo(() => {
        const access = credentials.access.accessTriples(clientConfig, 'full');
        return distinct(access?.map(a => a.type));
    }, [clientConfig, credentials]);

    const hideControl = useMemo(() => autoHide && perm === 'write' && userTypes.length === 1 && type && userTypes[0] === type && additionalTypes.length === 0, [additionalTypes.length, autoHide, perm, type, userTypes]);

    const displayTypes = useMemo(() => {
        return getDisplayTypes(types, credentials[credentialsRoot], site, level, type, perm, mode, filter, strict).concat(additionalTypes);
    }, [site, level, type, perm, credentials, credentialsRoot, filter, types, mode, strict, additionalTypes]);

    const handleChange = val => {
        let type = displayTypes.find(t => t.internalid === val);
        if (!type) {
            onChange({type: null});
        } else {
            let update = { type: type.type, color: type.color, icon: type.icon };
            if (strict && clientConfig.enableSites && type.site && type.site !== site) {
                update.site = type.site;
            }
            if (strict && clientConfig.enableLevels && type.level && type.level !== level) {
                update.level = type.level;
            }
            onChange(update);
        }
    }

    const options = useMemo(() => displayTypes.map(t => ({value: t.internalid, label: ((strict && clientConfig.enableSites && (!site || t.site !==site) && t.site) ? t.site + ' - ' : '') + ((strict && clientConfig.enableLevels && (!level || t.level !== level) && t.level) ? t.level + ' - ' : '') + t.type}) ), [displayTypes, clientConfig.enableSites, clientConfig.enableLevels, strict, site, level]);
    const typeDetails = displayTypes.find(t => t.type === type && (!strict || !clientConfig.enableSites || !t.site || t.site === site) && (!strict || !clientConfig.enableLevels || !t.level || t.level === level));
    
    if (hideControl) {
        return
    }
    if (readonly) {
        return <Label placeholder={placeholder} hideLabel={hideLabel} text={type ?? wildcard}></Label>
    }

    return <Select id={id} placeholder={placeholder ?? t('type', { count: 1 })} wildcard={wildcard} hideLabel={hideLabel} value={typeDetails?.internalid} onChange={handleChange} disabled={disabled} required={required} options={options} labelField="label" valueField="value"></Select>
}

const TypeDropdown = ({ ...props }) => <TypeDropdownImpl {...props}></TypeDropdownImpl>

export const RoleDropdown = ({ ...props }) => <TypeDropdownImpl {...props} additionalTypes={{ type: 'sitrep', site: null, level: null }}></TypeDropdownImpl>

TypeDropdown.propTypes = {
    /**
     * A site to use in filtering the available types
     */
    site: PropTypes.string,
    /**
     * A level to use in filtering the avialble types
     */
    level: PropTypes.string,
    /**
     * The currently selected type
     */
    type: PropTypes.string,
    /**
     * Only list types to which the user has appropriate access
     */
    perm: PropTypes.oneOf(['any','read','write']),
    /**
     * A filter function to gate whether a type is available as an option.
     * Takes a single argument - a type - and return true to include the type in
     * the available selections.
     */
    filter: PropTypes.func,
    /**
     * The label for the type dropdown
     */
    placeholder: PropTypes.string,
    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
    /**
     * A function called when the selected type is changed, it is called with
     * an object which will have a type filed set and may also have a site and/or
     * a level if the type has a more specific selection than passed in currently
     */
    onChange: PropTypes.func,
    /**
     * Used to filter types displayed based on usage since some types restricted from
     * being used to create jobs or prohibit job transfers.
     */
    mode: PropTypes.oneOf([null, 'create', 'transfer']),
    /**
     * When provided indicates the dropdown should allow a null selection which is to be treated as a wildcard.
     * The string supplied is used as the label of the wildcard entry (standard use would be something like
     * '(any)' or '(all)' depending on the context)
     */
    wildcard: PropTypes.string,
    /**
     * When set to false the dropdown will ignore site and level matching and only return the name of a type.
     * This is for use when wildcard matching against sites and levels is required - this situation requires strict = false
     */
    strict: PropTypes.bool
}

export default TypeDropdown;